import * as React from "react";
import { toast } from "sonner";
import { Trash2, Pencil, ChevronsDown, Check, MessagesSquare, Pin, Bookmark, Languages, ShieldAlert, Smile } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import EntityAvatar from "@/components/identity/EntityAvatar";

/**
 * MessageStream — dense, flat list. Consecutive messages from the same
 * sender within 3 minutes collapse to no header. Loads older on scroll-top.
 *
 * Live updates come through the global `fable:event` bus (SSE).
 */
const GROUP_WINDOW_MS = 3 * 60 * 1000;

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
function formatDayHeading(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yday = new Date(); yday.setDate(today.getDate() - 1);
  const same = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return "Today";
  if (same(d, yday)) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", day: "2-digit", month: "short" });
}

const MENTION_REGEX = /(@[A-Z][a-zA-Z]+(?:\s[A-Z][a-zA-Z]+)?|(?:shift|booking):[0-9a-fA-F-]{8,})/g;

function extractRefs(text) {
  const out = new Set();
  const s = String(text || "");
  const re = /(shift|booking):([0-9a-fA-F-]{8,})/g;
  let m;
  while ((m = re.exec(s)) !== null) out.add(`${m[1]}:${m[2]}`);
  return [...out];
}

function highlightBody(body, resolved) {
  // Render @mentions inline in violet + shift:/booking: chips resolved.
  const parts = String(body || "").split(MENTION_REGEX);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith("@")) {
      return <span key={i} className="text-brand-violet font-semibold bg-accent/70 px-1 rounded">{p}</span>;
    }
    if (p.startsWith("shift:") || p.startsWith("booking:")) {
      const r = resolved?.[p];
      if (!r) {
        return (
          <span key={i} className="chip mono-num text-[10.5px] mx-0.5" data-testid={`mention-unresolved-${p.replace(":","-")}`}>
            {p}
          </span>
        );
      }
      if (r.kind === "shift") {
        return (
          <span key={i} title={`${r.site_name || ""} · ${r.start.slice(0,10)} ${r.start.slice(11,16)}–${r.end.slice(11,16)} · ${r.member_name || 'Open'}`}
                className="inline-flex items-center gap-1 chip chip-violet mono-num text-[10.5px] mx-0.5"
                data-testid={`mention-shift-${r.id}`}>
            🕒 {r.start.slice(5,10)} {r.start.slice(11,16)}
            <span className="text-brand-muted">·</span>
            <span className="normal-case font-medium">{r.site_name || ""}</span>
            {r.member_name && <><span className="text-brand-muted">·</span><span className="normal-case">{r.member_name}</span></>}
          </span>
        );
      }
      if (r.kind === "booking") {
        return (
          <span key={i} title={`${r.service_name} · ${r.starts_at.slice(0,10)} ${r.starts_at.slice(11,16)} · ${r.status}`}
                className="inline-flex items-center gap-1 chip chip-amber mono-num text-[10.5px] mx-0.5"
                data-testid={`mention-booking-${r.id}`}>
            📅 {r.starts_at.slice(5,10)} {r.starts_at.slice(11,16)}
            <span className="text-brand-muted">·</span>
            <span className="normal-case font-medium">{r.customer_name}</span>
          </span>
        );
      }
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function MessageStream({ companyId, conversation, currentUserId, canManage, onNewSeen, onOpenThread, onQuoteReply }) {
  const convId = conversation.id;
  const [messages, setMessages] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingOlder, setLoadingOlder] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [resolved, setResolved] = React.useState({}); // ref -> metadata
  const streamRef = React.useRef(null);
  const bottomRef = React.useRef(null);

  // Batch-resolve shift:/booking: mentions as new bodies land.
  React.useEffect(() => {
    const refs = new Set();
    for (const m of messages) {
      for (const r of extractRefs(m.body)) {
        if (!resolved[r]) refs.add(r);
      }
    }
    if (refs.size === 0) return;
    let cancelled = false;
    api.post(`/companies/${companyId}/mentions/resolve`, { refs: [...refs] })
      .then(({data}) => {
        if (cancelled) return;
        setResolved((prev) => ({ ...prev, ...(data.resolved || {}) }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [messages, companyId, resolved]);

  const load = React.useCallback(async ({ before = null } = {}) => {
    try {
      const { data } = await api.get(
        `/companies/${companyId}/comms/conversations/${convId}/messages`,
        { params: before ? { before, limit: 50 } : { limit: 50 } }
      );
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load messages");
      return { messages: [], has_more: false };
    }
  }, [companyId, convId]);

  // Initial load
  React.useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);
    setMessages([]);
    load().then((data) => {
      if (cancelled) return;
      setMessages(data.messages || []);
      setHasMore(!!data.has_more);
      setInitialLoading(false);
      // Scroll to bottom on first load
      setTimeout(() => bottomRef.current?.scrollIntoView({ block: "end" }), 30);
    });
    return () => { cancelled = true; };
  }, [convId, load]);

  // Live: append incoming messages / update / delete
  React.useEffect(() => {
    const onEvt = (e) => {
      const evt = e.detail;
      if (!evt || evt.module !== "comms") return;
      const p = evt.payload || {};
      if (p.conversation_id !== convId) return;
      if (evt.type === "message.created") {
        setMessages((prev) => {
          if (prev.some((m) => m.id === p.message.id)) return prev;
          return [...prev, p.message];
        });
        // Auto-scroll if near bottom
        const el = streamRef.current;
        if (el) {
          const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
          if (nearBottom) {
            setTimeout(() => bottomRef.current?.scrollIntoView({ block: "end" }), 30);
            onNewSeen && onNewSeen();
          }
        }
      } else if (evt.type === "message.updated") {
        setMessages((prev) => prev.map((m) => m.id === p.message.id ? p.message : m));
      } else if (evt.type === "message.deleted") {
        setMessages((prev) => prev.map((m) => m.id === p.message_id ? {
          ...m, deleted: true, body: "", mention_user_ids: [], deleted_by_manage: p.by_manage,
        } : m));
      } else if (evt.type === "message.reaction") {
        setMessages((prev) => prev.map((m) => m.id === p.message_id ? {
          ...m, reactions: p.reactions || {},
        } : m));
      } else if (evt.type === "message.pinned") {
        setMessages((prev) => prev.map((m) => m.id === p.message_id ? {
          ...m, ...(p.message || {}), pinned: true,
        } : m));
      } else if (evt.type === "message.unpinned") {
        setMessages((prev) => prev.map((m) => m.id === p.message_id ? {
          ...m, pinned: false, pinned_by_name: null,
        } : m));
      } else if (evt.type === "message.thread_reply") {
        // Bump the root's reply counter locally
        setMessages((prev) => prev.map((m) => m.id === p.thread_root ? {
          ...m, thread_reply_count: (m.thread_reply_count || 0) + 1,
        } : m));
      } else if (evt.type === "poll.updated" || evt.type === "poll.closed") {
        setMessages((prev) => prev.map((m) => m.id === p.message_id ? (p.message || m) : m));
      }
    };
    window.addEventListener("fable:event", onEvt);

    // Phase 8 — optimistic message events from Composer.
    const onOpt = (e) => {
      const d = e.detail || {};
      if (d.conversation_id !== convId || !d.message) return;
      setMessages((prev) => (
        prev.some((m) => m.id === d.message.id) ? prev : [...prev, d.message]
      ));
      setTimeout(() => bottomRef.current?.scrollIntoView({ block: "end" }), 30);
    };
    const onOptSwap = (e) => {
      const d = e.detail || {};
      if (d.conversation_id !== convId) return;
      setMessages((prev) => {
        // If the confirmed message id is already in list (SSE beat us), drop the temp.
        const hasReal = prev.some((m) => m.id === d.message?.id);
        return prev
          .map((m) => (m.id === d.temp_id && !hasReal) ? d.message : m)
          .filter((m) => !(hasReal && m.id === d.temp_id));
      });
    };
    const onOptFail = (e) => {
      const d = e.detail || {};
      if (d.conversation_id !== convId) return;
      setMessages((prev) => prev.map((m) => m.id === d.temp_id ? { ...m, _failed: true, _pending: false } : m));
    };
    window.addEventListener("fable:optimistic-message", onOpt);
    window.addEventListener("fable:optimistic-swap", onOptSwap);
    window.addEventListener("fable:optimistic-fail", onOptFail);

    // Translation results — inline the translated body on the source message.
    const onTrans = (e) => {
      const d = e.detail || {};
      if (!d.message_id) return;
      setMessages((prev) => prev.map((m) => m.id === d.message_id ? {
        ...m,
        translations: { ...(m.translations || {}), [d.target_lang || "auto"]: d.translated },
      } : m));
    };
    window.addEventListener("fable:translation", onTrans);

    return () => {
      window.removeEventListener("fable:event", onEvt);
      window.removeEventListener("fable:optimistic-message", onOpt);
      window.removeEventListener("fable:optimistic-swap", onOptSwap);
      window.removeEventListener("fable:optimistic-fail", onOptFail);
      window.removeEventListener("fable:translation", onTrans);
    };
  }, [convId, onNewSeen]);

  // Load older when scrolled to top
  const onScroll = React.useCallback(async () => {
    const el = streamRef.current;
    if (!el || loadingOlder || !hasMore) return;
    if (el.scrollTop < 40 && messages.length > 0) {
      setLoadingOlder(true);
      const oldest = messages[0];
      const prevHeight = el.scrollHeight;
      const data = await load({ before: oldest.id });
      setMessages((prev) => [...(data.messages || []), ...prev]);
      setHasMore(!!data.has_more);
      // Preserve scroll position after older loaded
      requestAnimationFrame(() => {
        const el2 = streamRef.current;
        if (el2) el2.scrollTop = el2.scrollHeight - prevHeight;
        setLoadingOlder(false);
      });
    }
  }, [loadingOlder, hasMore, messages, load]);

  // Group messages: attach `_groupHead=true` when the sender/time changes.
  const withGrouping = React.useMemo(() => {
    let lastSender = null;
    let lastTs = 0;
    let lastDay = null;
    return (messages || []).map((m, i) => {
      const ts = new Date(m.created_at).getTime();
      const day = new Date(m.created_at).toDateString();
      const isNewSender = m.sender_id !== lastSender;
      const isFar = ts - lastTs > GROUP_WINDOW_MS;
      const isNewDay = day !== lastDay;
      const head = isNewSender || isFar || isNewDay;
      lastSender = m.sender_id;
      lastTs = ts;
      const showDayDivider = isNewDay;
      lastDay = day;
      return { ...m, _head: head, _dayDivider: showDayDivider };
    });
  }, [messages]);

  const canDeleteMessage = React.useCallback((m) => {
    if (m.deleted) return false;
    if (m.sender_id === currentUserId) return true;
    if (canManage && conversation.kind === "channel") return true;
    return false;
  }, [currentUserId, canManage, conversation.kind]);

  const canEditMessage = React.useCallback((m) => {
    if (m.deleted) return false;
    return m.sender_id === currentUserId;
  }, [currentUserId]);

  const [editingId, setEditingId] = React.useState(null);
  const [editingDraft, setEditingDraft] = React.useState("");

  async function saveEdit(m) {
    const body = editingDraft.trim();
    if (!body || body === m.body) { setEditingId(null); return; }
    try {
      await api.patch(
        `/companies/${companyId}/comms/conversations/${convId}/messages/${m.id}`,
        { body },
      );
      setEditingId(null); setEditingDraft("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update");
    }
  }

  async function deleteMsg(m) {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/companies/${companyId}/comms/conversations/${convId}/messages/${m.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete");
    }
  }

  return (
    <div
      ref={streamRef}
      className="flex-1 min-h-0 overflow-y-auto px-4 py-3 relative"
      onScroll={onScroll}
      data-testid="comms-stream"
    >
      {loadingOlder && (
        <div className="text-center text-[11px] text-brand-muted py-2" data-testid="comms-stream-loading-older">
          Loading older messages…
        </div>
      )}
      {!hasMore && messages.length > 0 && (
        <div className="text-center text-[10.5px] text-brand-faint py-2 mono-num tracking-[0.14em] uppercase">
          Start of conversation
        </div>
      )}
      {initialLoading ? (
        <div className="text-center text-[12px] text-brand-muted py-8" data-testid="comms-stream-loading">
          Loading…
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center text-[12.5px] text-brand-muted py-8" data-testid="comms-stream-empty">
          No messages yet. Say hello.
        </div>
      ) : (
        withGrouping.map((m) => (
          <React.Fragment key={m.id}>
            {m._dayDivider && (
              <div className="my-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-brand-rule" />
                <div className="mono-num text-[10.5px] tracking-[0.16em] uppercase text-brand-muted">
                  {formatDayHeading(m.created_at)}
                </div>
                <div className="h-px flex-1 bg-brand-rule" />
              </div>
            )}
            <div
              className={`group flex gap-3 py-0.5 ${m._head ? "mt-2" : ""}`}
              data-testid={`comms-msg-${m.id}`}
              data-msg-pending={m._pending ? "true" : undefined}
              data-msg-failed={m._failed ? "true" : undefined}
            >
              {m._head ? (
                <EntityAvatar
                  kind="person"
                  name={m.sender_name || "?"}
                  id={m.sender_id || m.sender_name}
                  size={28}
                  className="shrink-0"
                  testId={`comms-msg-${m.id}-avatar`}
                />
              ) : (
                <div className="w-7 shrink-0 text-right pr-1">
                  <span className="mono-num text-[9.5px] text-transparent group-hover:text-brand-faint">
                    {formatTime(m.created_at)}
                  </span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                {m._head && (
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="text-[12.5px] font-semibold text-brand-ink truncate">
                      {m.sender_name}
                    </div>
                    <div className="mono-num text-[10.5px] text-brand-muted">
                      {formatTime(m.created_at)}
                    </div>
                    {m.edited_at && !m.deleted && (
                      <span className="mono-num text-[9.5px] text-brand-faint">edited</span>
                    )}
                    {m.priority === "important" && (
                      <span className="chip chip-amber mono-num text-[9.5px]" data-testid={`comms-msg-${m.id}-priority`}>important</span>
                    )}
                    {m.priority === "urgent" && (
                      <span className="chip chip-coral mono-num text-[9.5px]" data-testid={`comms-msg-${m.id}-priority`}>urgent</span>
                    )}
                    {m.priority === "emergency" && (
                      <span className="chip chip-coral mono-num text-[9.5px]" data-testid={`comms-msg-${m.id}-priority`}>emergency</span>
                    )}
                    {m.requires_ack && (
                      <span className="chip chip-violet mono-num text-[9.5px]">ack required · {m.ack_count || 0}/{m.ack_target_count || 0}</span>
                    )}
                  </div>
                )}
                {editingId === m.id ? (
                  <div className="mt-1">
                    <textarea
                      className="field-input min-h-[68px] py-2 w-full text-[13px]"
                      value={editingDraft}
                      onChange={(e) => setEditingDraft(e.target.value)}
                      autoFocus
                      data-testid={`comms-msg-${m.id}-edit`}
                    />
                    <div className="mt-1 flex gap-2 items-center">
                      <button type="button" onClick={() => saveEdit(m)} className="btn-violet h-7 text-[11.5px]">Save</button>
                      <button type="button" onClick={() => { setEditingId(null); setEditingDraft(""); }} className="btn-ghost h-7 text-[11.5px]">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className={`msg-body text-[13px] text-brand-ink whitespace-pre-wrap break-words ${m.deleted ? "italic text-brand-faint" : ""}`}>
                    {m.deleted
                      ? (m.deleted_by_manage ? "[message removed by a moderator]" : "[message deleted]")
                      : highlightBody(m.body, resolved)}
                    {m._failed && (
                      <span className="ml-1.5 chip chip-coral mono-num text-[9.5px]">failed</span>
                    )}
                  </div>
                )}
                {m.kind === "poll" && m.poll && !m.deleted && (
                  <PollCard
                    companyId={companyId} convId={convId} msg={m}
                    currentUserId={currentUserId}
                  />
                )}
                {m.kind === "recognition" && m.recognition && !m.deleted && (
                  <div className="mt-1 rounded-md border border-brand-rule bg-accent/40 px-2.5 py-1.5 text-[12px] text-brand-ink"
                       data-testid={`comms-msg-${m.id}-recognition`}>
                    <span className="mono-num text-[10px] uppercase tracking-wider text-brand-violet mr-1.5">
                      {m.recognition.tag}
                    </span>
                    <strong>{m.recognition.recipient_name}</strong> — {m.recognition.note}
                  </div>
                )}
                {m.dlp_status === "quarantined" && !m.deleted && (
                  <div className="mt-1.5 rounded-md border border-amber-400/60 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900 flex items-center gap-1.5"
                       data-testid={`comms-msg-${m.id}-dlp`}>
                    <ShieldAlert size={12} />
                    <span>Quarantined — sensitive content was redacted before send.</span>
                    {(m.dlp_rules_hit || []).length > 0 && (
                      <span className="mono-num text-[9.5px] text-amber-700">
                        {(m.dlp_rules_hit || []).join(", ")}
                      </span>
                    )}
                  </div>
                )}
                {m.pinned && !m.deleted && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-brand-muted"
                       data-testid={`comms-msg-${m.id}-pinned`}>
                    <Pin size={10} /> Pinned by {m.pinned_by_name || "manager"}
                  </div>
                )}
                {!m.deleted && !m._pending && (m.thread_reply_count > 0) && (
                  <button
                    type="button"
                    onClick={() => onOpenThread && onOpenThread(m.id)}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-violet hover:underline"
                    data-testid={`comms-msg-${m.id}-open-thread`}
                  >
                    <MessagesSquare size={11} /> {m.thread_reply_count} repl{m.thread_reply_count === 1 ? "y" : "ies"}
                  </button>
                )}
                {!m.deleted && !m._pending && m.reactions && Object.keys(m.reactions).length > 0 && (
                  <ReactionsRow
                    companyId={companyId} convId={convId} msg={m}
                    currentUserId={currentUserId}
                  />
                )}
                {m.translations && Object.keys(m.translations).length > 0 && (
                  <div className="mt-1 text-[12px] text-brand-muted italic bg-brand-hush/60 px-2 py-1 rounded">
                    {Object.values(m.translations)[0]}
                  </div>
                )}
                {m.requires_ack && !m.deleted && !m._pending && (
                  <AckStrip
                    companyId={companyId}
                    msg={m}
                    currentUserId={currentUserId}
                    canManage={canManage}
                  />
                )}
              </div>
              {editingId !== m.id && (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-start gap-0.5 shrink-0">
                  {!m.deleted && !m._pending && (
                    <QuickReactionButton companyId={companyId} convId={convId} msg={m} />
                  )}
                  {!m.deleted && !m._pending && (
                    <button
                      type="button"
                      onClick={() => onOpenThread && onOpenThread(m.id)}
                      className="btn-ghost h-6 w-6 !p-0"
                      title="Reply in thread"
                      aria-label="Reply in thread"
                      data-testid={`comms-msg-${m.id}-thread-btn`}
                    >
                      <MessagesSquare size={11} />
                    </button>
                  )}
                  {!m.deleted && !m._pending && (
                    <TranslateButton companyId={companyId} convId={convId} msg={m} />
                  )}
                  {!m.deleted && !m._pending && (
                    <SaveButton companyId={companyId} msg={m} />
                  )}
                  {!m.deleted && !m._pending && canManage && (
                    <PinButton companyId={companyId} convId={convId} msg={m} />
                  )}
                  {canEditMessage(m) && (
                    <button
                      type="button"
                      onClick={() => { setEditingId(m.id); setEditingDraft(m.body); }}
                      className="btn-ghost h-6 w-6 !p-0"
                      data-testid={`comms-msg-${m.id}-edit-trigger`}
                      title="Edit"
                    >
                      <Pencil size={11} />
                    </button>
                  )}
                  {canDeleteMessage(m) && (
                    <button
                      type="button"
                      onClick={() => deleteMsg(m)}
                      className="btn-ghost h-6 w-6 !p-0"
                      data-testid={`comms-msg-${m.id}-delete`}
                      title="Delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>
        ))
      )}
      <div ref={bottomRef} />
    </div>
  );
}



function AckStrip({ companyId, msg, currentUserId, canManage }) {
  const [busy, setBusy] = React.useState(false);
  const [ackedNow, setAckedNow] = React.useState(false);
  const targets = msg.ack_target_user_ids || [];
  const isTarget = targets.includes(currentUserId);
  const total = msg.ack_target_count || targets.length || 0;
  const count = (msg.ack_count || 0) + (ackedNow ? 1 : 0);
  const alreadyAcked = ackedNow;

  async function ack() {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/comms/messages/${msg.id}/ack`, {});
      setAckedNow(true);
      toast.success("Acknowledged");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't ack");
    } finally { setBusy(false); }
  }

  function downloadCsv() {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/companies/${msg.company_id}/comms/messages/${msg.id}/ack-roster.csv`;
    window.open(url, "_blank");
  }

  return (
    <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`comms-msg-${msg.id}-ack-strip`}>
      {isTarget && !alreadyAcked && (
        <button
          type="button" disabled={busy} onClick={ack}
          className="btn-violet h-7 text-[11.5px]"
          data-testid={`comms-msg-${msg.id}-ack`}
        >
          <Check size={12} /> Acknowledge
        </button>
      )}
      {isTarget && alreadyAcked && (
        <span className="chip chip-emerald mono-num text-[10.5px]">acknowledged</span>
      )}
      <span className="text-[11px] text-brand-muted mono-num" data-testid={`comms-msg-${msg.id}-ack-count`}>
        {count}/{total} acknowledged
      </span>
      {canManage && (
        <button
          type="button" onClick={downloadCsv}
          className="btn-outline h-7 text-[11px]"
          data-testid={`comms-msg-${msg.id}-ack-csv`}
        >
          Export CSV
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Phase 13.2 helper components
// ────────────────────────────────────────────────────────────────

const QUICK_REACTIONS = ["thumbs_up", "clap", "eyes", "check"];
const REACTION_LABEL = {
  like: "👍", love: "❤️", laugh: "😄", clap: "👏", eyes: "👀",
  raised_hands: "🙌", thumbs_up: "👍", check: "✅", warn: "⚠️",
};

function QuickReactionButton({ companyId, convId, msg }) {
  const [open, setOpen] = React.useState(false);
  async function toggle(emoji) {
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${convId}/messages/${msg.id}/reactions`,
        { emoji },
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't react");
    }
    setOpen(false);
  }
  return (
    <div className="relative">
      <button
        type="button"
        className="btn-ghost h-6 w-6 !p-0"
        onClick={() => setOpen((v) => !v)}
        title="React"
        aria-label="React"
        data-testid={`comms-msg-${msg.id}-react-btn`}
      >
        <Smile size={11} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 bg-card border border-brand-rule rounded-md shadow-card px-1 py-0.5 flex gap-0.5"
          data-testid={`comms-msg-${msg.id}-react-picker`}
        >
          {QUICK_REACTIONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => toggle(e)}
              className="text-[15px] leading-none px-1.5 py-1 hover:bg-brand-hush rounded"
              data-testid={`comms-msg-${msg.id}-react-${e}`}
              aria-label={`React ${e}`}
            >
              {REACTION_LABEL[e]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReactionsRow({ companyId, convId, msg, currentUserId }) {
  async function toggle(emoji) {
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${convId}/messages/${msg.id}/reactions`,
        { emoji },
      );
    } catch { /* silent */ }
  }
  const entries = Object.entries(msg.reactions || {})
    .filter(([, uids]) => (uids || []).length > 0);
  if (entries.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid={`comms-msg-${msg.id}-reactions`}>
      {entries.map(([emoji, uids]) => {
        const mine = uids.includes(currentUserId);
        return (
          <button
            key={emoji}
            type="button"
            onClick={() => toggle(emoji)}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border ${
              mine
                ? "border-brand-violet bg-accent text-brand-violet"
                : "border-brand-rule bg-card text-brand-ink hover:bg-brand-hush"
            }`}
            data-testid={`comms-msg-${msg.id}-reaction-${emoji}`}
          >
            <span className="leading-none text-[13px]">{REACTION_LABEL[emoji] || emoji}</span>
            <span className="mono-num text-[10px]">{uids.length}</span>
          </button>
        );
      })}
    </div>
  );
}

function PinButton({ companyId, convId, msg }) {
  const [busy, setBusy] = React.useState(false);
  async function toggle() {
    setBusy(true);
    try {
      if (msg.pinned) {
        await api.delete(`/companies/${companyId}/comms/conversations/${convId}/messages/${msg.id}/pin`);
        toast.success("Unpinned");
      } else {
        await api.post(`/companies/${companyId}/comms/conversations/${convId}/messages/${msg.id}/pin`, {});
        toast.success("Pinned");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't pin");
    } finally { setBusy(false); }
  }
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`btn-ghost h-6 w-6 !p-0 ${msg.pinned ? "text-brand-violet" : ""}`}
      title={msg.pinned ? "Unpin" : "Pin"}
      aria-label={msg.pinned ? "Unpin message" : "Pin message"}
      data-testid={`comms-msg-${msg.id}-pin-btn`}
    >
      <Pin size={11} />
    </button>
  );
}

function SaveButton({ companyId, msg }) {
  const [saved, setSaved] = React.useState(!!msg._saved);
  const [busy, setBusy] = React.useState(false);
  async function toggle() {
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/companies/${companyId}/comms/messages/${msg.id}/save`);
        setSaved(false);
      } else {
        await api.post(`/companies/${companyId}/comms/messages/${msg.id}/save`, {});
        setSaved(true);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  }
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`btn-ghost h-6 w-6 !p-0 ${saved ? "text-brand-violet" : ""}`}
      title={saved ? "Unsave" : "Save"}
      aria-label={saved ? "Unsave message" : "Save message"}
      data-testid={`comms-msg-${msg.id}-save-btn`}
    >
      <Bookmark size={11} />
    </button>
  );
}

function TranslateButton({ companyId, convId, msg }) {
  const [busy, setBusy] = React.useState(false);
  async function run() {
    setBusy(true);
    try {
      // Pull the user's preferred language; default to 'en'
      let target = "en";
      try {
        const { data } = await api.get(`/companies/${companyId}/comms/prefs`);
        target = data?.preferred_language || "en";
      } catch {}
      const { data } = await api.post(
        `/companies/${companyId}/comms/conversations/${convId}/messages/${msg.id}/translate`,
        { target_lang: target },
      );
      window.dispatchEvent(new CustomEvent("fable:translation", {
        detail: { message_id: msg.id, target_lang: target, translated: data.translated },
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Translation failed");
    } finally { setBusy(false); }
  }
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="btn-ghost h-6 w-6 !p-0"
      title="Translate"
      aria-label="Translate"
      data-testid={`comms-msg-${msg.id}-translate-btn`}
    >
      <Languages size={11} />
    </button>
  );
}

function PollCard({ companyId, convId, msg, currentUserId }) {
  const poll = msg.poll || {};
  const options = poll.options || [];
  const tally = poll.tally || {};
  const total = Object.values(tally).reduce((s, n) => s + (n || 0), 0);
  const closed = !!poll.closed_at;
  const alreadyVoted = (poll.voter_ids || []).includes(currentUserId);
  const canClose = !closed && (msg.sender_id === currentUserId);
  async function vote(oid) {
    if (closed || alreadyVoted) return;
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${convId}/messages/${msg.id}/poll/vote`,
        { option_ids: [oid] },
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't vote");
    }
  }
  async function close() {
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${convId}/messages/${msg.id}/poll/close`, {},
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't close");
    }
  }
  return (
    <div className="mt-1.5 rounded-md border border-brand-rule bg-card p-3 max-w-[420px]"
         data-testid={`comms-msg-${msg.id}-poll`}>
      <div className="text-[12.5px] font-semibold text-brand-ink mb-2">{poll.question}</div>
      <div className="space-y-1.5">
        {options.map((o) => {
          const cnt = tally[o.id] || 0;
          const pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => vote(o.id)}
              disabled={closed || alreadyVoted}
              className="w-full text-left relative rounded border border-brand-rule hover:bg-brand-hush disabled:cursor-default disabled:hover:bg-transparent"
              data-testid={`comms-msg-${msg.id}-poll-${o.id}`}
            >
              <div
                className="absolute inset-y-0 left-0 bg-accent/60 rounded"
                style={{ width: `${pct}%` }}
              />
              <div className="relative flex items-center justify-between px-2.5 py-1.5">
                <span className="text-[12.5px] text-brand-ink">{o.label}</span>
                <span className="mono-num text-[10.5px] text-brand-muted">
                  {cnt} · {pct}%
                </span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10.5px] text-brand-muted">
        <span data-testid={`comms-msg-${msg.id}-poll-total`}>
          {total} vote{total === 1 ? "" : "s"}{closed ? " · closed" : ""}
        </span>
        {canClose && (
          <button
            type="button"
            onClick={close}
            className="btn-ghost h-6 text-[11px]"
            data-testid={`comms-msg-${msg.id}-poll-close`}
          >
            Close poll
          </button>
        )}
      </div>
    </div>
  );
}
